import {
  Component,
  computed,
  ElementRef,
  EventEmitter,
  inject,
  input,
  Input,
  InputSignal,
  Output,
  Signal,
  signal,
  OnInit,
  OnDestroy,
} from '@angular/core';
import _ from 'lodash';
import { BehaviorSubject } from 'rxjs';
import { SortColumn } from 'src/app/models/sortColumn.model';
import { ViolationRulesTableColumns } from 'src/app/models/tableColumns/violationRulesColumns.model';
import { ViolationRulesViewModel } from 'src/app/models/viewModels/violationRulesViewModel';
import { RowActionClicked, RowClicked } from '../table-row/table-row.component';
import { ActivatedRoute } from '@angular/router';
import { SidePanelService } from '../../features/side-panel/services/side-panel.service';
import { RuleStatus, RuleTag } from 'src/app/models/api/governance/violationRule.model';
import { StatusLabelEnum } from '../status-label/status-label.component';
import { FeatureFlagsService } from 'src/app/services/infra/featureFlags/feature-flags.service';
import { FeatureFlagTypes } from 'src/app/models/featureFlag/featureFlagsTypes.enum';

@Component({
  selector: 'app-violation-rules-table',
  templateUrl: './violation-rules-table.component.html',
  styleUrls: ['./violation-rules-table.component.scss'],
})
export class ViolationRulesTableComponent implements OnInit, OnDestroy {
  public isLoading = input<boolean>(false);

  currentRulesViewModel: InputSignal<BehaviorSubject<BehaviorSubject<ViolationRulesViewModel>[]>> = input.required();

  @Input() public categoryDisplayName?: string;
  @Input() public categoryCode?: string;
  @Input() public categoryLink?: string;

  @Output() public rowActionOnClick = new EventEmitter<RowActionClicked>();

  public tableData: Signal<BehaviorSubject<BehaviorSubject<ViolationRulesViewModel>[]>>;
  public tableColumns = ViolationRulesTableColumns;
  public sortedColumn = signal<SortColumn | undefined>(undefined);
  public sorting: Signal<{ sorting: 'violations' | 'severity'; isDesc: boolean } | null>;
  public isEmptyTable: Signal<boolean>;

  private readonly _featureFlagsService = inject(FeatureFlagsService);
  private readonly _elementRef = inject(ElementRef);
  private _ruleUpdateListener: ((event: Event) => void) | null = null;
  private _navigationClickListener: ((event: Event) => void) | null = null;

  constructor(
    private readonly _activeRoute: ActivatedRoute,
    private readonly _sidePanel: SidePanelService
  ) {
    this.tableData = computed(() => {
      const sort = this.sorting();
      if (!sort) {
        return this.currentRulesViewModel();
      }

      const isDescStr = sort.isDesc ? 'desc' : 'asc';
      switch (sort.sorting) {
        case 'violations':
          return new BehaviorSubject(_.orderBy(this.currentRulesViewModel().value, ['value.violations'], [isDescStr]));
        case 'severity':
          return new BehaviorSubject(
            _.orderBy(this.currentRulesViewModel().value, ['value.severityValue'], [isDescStr])
          );
      }
    });

    this.isEmptyTable = computed(() => !this.tableData().value.length);

    this.sorting = computed(() => {
      const sort = this.sortedColumn();
      if (!sort) return null;

      if (sort.column.sortKey === 'violations') {
        return { sorting: 'violations', isDesc: sort.isDesc };
      } else if (sort.column.sortKey === 'severity') {
        return { sorting: 'severity', isDesc: sort.isDesc };
      }

      return null;
    });
  }

  ngOnInit(): void {
    // Close side panel when clicking on internal navigation links (violations count)
    this._navigationClickListener = (event: Event) => {
      const target = event.target as HTMLElement;
      if (target.classList.contains('text-navigation')) {
        this._sidePanel.close();
      }
    };
    this._elementRef.nativeElement.addEventListener('click', this._navigationClickListener, true);

    // Listen for violation rule updates from the side panel
    this._ruleUpdateListener = ((event: CustomEvent) => {
      const { ruleId, severity, status } = event.detail;

      // Find and update the specific rule in the table data
      const rows = this.currentRulesViewModel().value;
      const rowToUpdate = rows.find((row) => row.value.ruleId === ruleId);

      if (rowToUpdate) {
        const currentValue = rowToUpdate.value;

        // Update severity if provided
        if (severity !== undefined) {
          currentValue.severity = severity;
          const severityValues: Record<string, number> = {
            critical: 4,
            high: 3,
            medium: 2,
            low: 1,
            informational: 0,
          };
          currentValue.severityValue = severityValues[severity] || 0;
        }

        // Update status if provided
        if (status !== undefined) {
          currentValue.status = status;
          // Update statusLabel to match the new status
          const statusLabels: Record<RuleStatus, StatusLabelEnum> = {
            [RuleStatus.on]: StatusLabelEnum.active,
            [RuleStatus.off]: StatusLabelEnum.inactive,
            [RuleStatus.unavailable]: StatusLabelEnum.inactive,
          };
          currentValue.statusLabel = statusLabels[status as RuleStatus];
        }

        // Trigger the BehaviorSubject to emit with the updated value
        rowToUpdate.next(currentValue);
      }
    }) as EventListener;

    window.addEventListener('violation-rule-updated', this._ruleUpdateListener);
  }

  ngOnDestroy(): void {
    if (this._ruleUpdateListener) {
      window.removeEventListener('violation-rule-updated', this._ruleUpdateListener);
    }
    if (this._navigationClickListener) {
      this._elementRef.nativeElement.removeEventListener('click', this._navigationClickListener, true);
    }
  }

  public actionOnClick(actionClicked: RowActionClicked): void {
    this.rowActionOnClick.emit(actionClicked);
  }

  //Update Sorting (called from Child)
  public onSortedColumnChange(newSortColumn: SortColumn): void {
    this.sortedColumn.set(newSortColumn);
  }

  public onRowClicked(event: RowClicked): void {
    const vm = event.row.getValue() as ViolationRulesViewModel;
    const integrationId =
      vm.integrationId ??
      (vm.violationsEndpointQuery as Record<string, string>)?.['integration'] ??
      this._activeRoute.snapshot.queryParamMap.get('integration');
    const policyId =
      vm.policyId ??
      (vm.violationsEndpointQuery as Record<string, string>)?.['policy'] ??
      this._activeRoute.snapshot.queryParamMap.get('policy');

    if (!integrationId || !policyId) {
      return;
    }

    const ruleType = vm.tags?.includes(RuleTag.AIDR) ? 'detection' : 'violation';
    this._sidePanel.openViolationRuleDetails(vm.ruleId, integrationId, policyId, vm, ruleType);
  }
}
